// The ECMAScript lib definitions fed to the Monaco editor for filter-code
// IntelliSense. Explicit `?raw` imports for each `lib.*.d.ts` in the ES2024
// chain from the installed TypeScript package.
//
// Why explicit rather than `import.meta.glob`? Vite production builds don't
// scan `node_modules` for glob patterns (dev did, so this bug only surfaced
// post-deploy — sc-90467 tail). Explicit `?raw` imports use bare-package
// specifiers which resolve reliably in both modes.
//
// Excluded (same as the runtime_scripting_lambdas `loadAllTsLibs`):
//   - `lib.d.ts` (umbrella), `*.full.d.ts` (DOM-bearing)
//   - `lib.dom*`, `lib.webworker*`, `lib.scripthost*` (filter code has no DOM)
//   - `lib.esnext.*` (unratified proposals the runtime compiler won't accept)
//   - `lib.es6.d.ts` (alias for `lib.es2015.d.ts` — double-register)
//
// Keep this list in sync with `loadAllTsLibs()` in
// `runtime_scripting_lambdas/handlers/typescript_compiler.js` when bumping
// the TypeScript version.

import decoratorsLib from "typescript/lib/lib.decorators.d.ts?raw"
import decoratorsLegacyLib from "typescript/lib/lib.decorators.legacy.d.ts?raw"

import es5Lib from "typescript/lib/lib.es5.d.ts?raw"

import es2015Lib from "typescript/lib/lib.es2015.d.ts?raw"
import es2015CollectionLib from "typescript/lib/lib.es2015.collection.d.ts?raw"
import es2015CoreLib from "typescript/lib/lib.es2015.core.d.ts?raw"
import es2015GeneratorLib from "typescript/lib/lib.es2015.generator.d.ts?raw"
import es2015IterableLib from "typescript/lib/lib.es2015.iterable.d.ts?raw"
import es2015PromiseLib from "typescript/lib/lib.es2015.promise.d.ts?raw"
import es2015ProxyLib from "typescript/lib/lib.es2015.proxy.d.ts?raw"
import es2015ReflectLib from "typescript/lib/lib.es2015.reflect.d.ts?raw"
import es2015SymbolLib from "typescript/lib/lib.es2015.symbol.d.ts?raw"
import es2015SymbolWellknownLib from "typescript/lib/lib.es2015.symbol.wellknown.d.ts?raw"

import es2016Lib from "typescript/lib/lib.es2016.d.ts?raw"
import es2016ArrayIncludeLib from "typescript/lib/lib.es2016.array.include.d.ts?raw"
import es2016IntlLib from "typescript/lib/lib.es2016.intl.d.ts?raw"

import es2017Lib from "typescript/lib/lib.es2017.d.ts?raw"
import es2017ArraybufferLib from "typescript/lib/lib.es2017.arraybuffer.d.ts?raw"
import es2017DateLib from "typescript/lib/lib.es2017.date.d.ts?raw"
import es2017IntlLib from "typescript/lib/lib.es2017.intl.d.ts?raw"
import es2017ObjectLib from "typescript/lib/lib.es2017.object.d.ts?raw"
import es2017SharedmemoryLib from "typescript/lib/lib.es2017.sharedmemory.d.ts?raw"
import es2017StringLib from "typescript/lib/lib.es2017.string.d.ts?raw"
import es2017TypedarraysLib from "typescript/lib/lib.es2017.typedarrays.d.ts?raw"

import es2018Lib from "typescript/lib/lib.es2018.d.ts?raw"
import es2018AsyncgeneratorLib from "typescript/lib/lib.es2018.asyncgenerator.d.ts?raw"
import es2018AsynciterableLib from "typescript/lib/lib.es2018.asynciterable.d.ts?raw"
import es2018IntlLib from "typescript/lib/lib.es2018.intl.d.ts?raw"
import es2018PromiseLib from "typescript/lib/lib.es2018.promise.d.ts?raw"
import es2018RegexpLib from "typescript/lib/lib.es2018.regexp.d.ts?raw"

import es2019Lib from "typescript/lib/lib.es2019.d.ts?raw"
import es2019ArrayLib from "typescript/lib/lib.es2019.array.d.ts?raw"
import es2019IntlLib from "typescript/lib/lib.es2019.intl.d.ts?raw"
import es2019ObjectLib from "typescript/lib/lib.es2019.object.d.ts?raw"
import es2019StringLib from "typescript/lib/lib.es2019.string.d.ts?raw"
import es2019SymbolLib from "typescript/lib/lib.es2019.symbol.d.ts?raw"

import es2020Lib from "typescript/lib/lib.es2020.d.ts?raw"
import es2020BigintLib from "typescript/lib/lib.es2020.bigint.d.ts?raw"
import es2020DateLib from "typescript/lib/lib.es2020.date.d.ts?raw"
import es2020IntlLib from "typescript/lib/lib.es2020.intl.d.ts?raw"
import es2020NumberLib from "typescript/lib/lib.es2020.number.d.ts?raw"
import es2020PromiseLib from "typescript/lib/lib.es2020.promise.d.ts?raw"
import es2020SharedmemoryLib from "typescript/lib/lib.es2020.sharedmemory.d.ts?raw"
import es2020StringLib from "typescript/lib/lib.es2020.string.d.ts?raw"
import es2020SymbolWellknownLib from "typescript/lib/lib.es2020.symbol.wellknown.d.ts?raw"

import es2021Lib from "typescript/lib/lib.es2021.d.ts?raw"
import es2021IntlLib from "typescript/lib/lib.es2021.intl.d.ts?raw"
import es2021PromiseLib from "typescript/lib/lib.es2021.promise.d.ts?raw"
import es2021StringLib from "typescript/lib/lib.es2021.string.d.ts?raw"
import es2021WeakrefLib from "typescript/lib/lib.es2021.weakref.d.ts?raw"

import es2022Lib from "typescript/lib/lib.es2022.d.ts?raw"
import es2022ArrayLib from "typescript/lib/lib.es2022.array.d.ts?raw"
import es2022ErrorLib from "typescript/lib/lib.es2022.error.d.ts?raw"
import es2022IntlLib from "typescript/lib/lib.es2022.intl.d.ts?raw"
import es2022ObjectLib from "typescript/lib/lib.es2022.object.d.ts?raw"
import es2022RegexpLib from "typescript/lib/lib.es2022.regexp.d.ts?raw"
import es2022StringLib from "typescript/lib/lib.es2022.string.d.ts?raw"

import es2023Lib from "typescript/lib/lib.es2023.d.ts?raw"
import es2023ArrayLib from "typescript/lib/lib.es2023.array.d.ts?raw"
import es2023CollectionLib from "typescript/lib/lib.es2023.collection.d.ts?raw"
import es2023IntlLib from "typescript/lib/lib.es2023.intl.d.ts?raw"

import es2024Lib from "typescript/lib/lib.es2024.d.ts?raw"
import es2024ArraybufferLib from "typescript/lib/lib.es2024.arraybuffer.d.ts?raw"
import es2024CollectionLib from "typescript/lib/lib.es2024.collection.d.ts?raw"
import es2024ObjectLib from "typescript/lib/lib.es2024.object.d.ts?raw"
import es2024PromiseLib from "typescript/lib/lib.es2024.promise.d.ts?raw"
import es2024RegexpLib from "typescript/lib/lib.es2024.regexp.d.ts?raw"
import es2024SharedmemoryLib from "typescript/lib/lib.es2024.sharedmemory.d.ts?raw"
import es2024StringLib from "typescript/lib/lib.es2024.string.d.ts?raw"

const esLibDefs = {
  "lib.decorators.d.ts": decoratorsLib,
  "lib.decorators.legacy.d.ts": decoratorsLegacyLib,

  "lib.es5.d.ts": es5Lib,

  "lib.es2015.d.ts": es2015Lib,
  "lib.es2015.collection.d.ts": es2015CollectionLib,
  "lib.es2015.core.d.ts": es2015CoreLib,
  "lib.es2015.generator.d.ts": es2015GeneratorLib,
  "lib.es2015.iterable.d.ts": es2015IterableLib,
  "lib.es2015.promise.d.ts": es2015PromiseLib,
  "lib.es2015.proxy.d.ts": es2015ProxyLib,
  "lib.es2015.reflect.d.ts": es2015ReflectLib,
  "lib.es2015.symbol.d.ts": es2015SymbolLib,
  "lib.es2015.symbol.wellknown.d.ts": es2015SymbolWellknownLib,

  "lib.es2016.d.ts": es2016Lib,
  "lib.es2016.array.include.d.ts": es2016ArrayIncludeLib,
  "lib.es2016.intl.d.ts": es2016IntlLib,

  "lib.es2017.d.ts": es2017Lib,
  "lib.es2017.arraybuffer.d.ts": es2017ArraybufferLib,
  "lib.es2017.date.d.ts": es2017DateLib,
  "lib.es2017.intl.d.ts": es2017IntlLib,
  "lib.es2017.object.d.ts": es2017ObjectLib,
  "lib.es2017.sharedmemory.d.ts": es2017SharedmemoryLib,
  "lib.es2017.string.d.ts": es2017StringLib,
  "lib.es2017.typedarrays.d.ts": es2017TypedarraysLib,

  "lib.es2018.d.ts": es2018Lib,
  "lib.es2018.asyncgenerator.d.ts": es2018AsyncgeneratorLib,
  "lib.es2018.asynciterable.d.ts": es2018AsynciterableLib,
  "lib.es2018.intl.d.ts": es2018IntlLib,
  "lib.es2018.promise.d.ts": es2018PromiseLib,
  "lib.es2018.regexp.d.ts": es2018RegexpLib,

  "lib.es2019.d.ts": es2019Lib,
  "lib.es2019.array.d.ts": es2019ArrayLib,
  "lib.es2019.intl.d.ts": es2019IntlLib,
  "lib.es2019.object.d.ts": es2019ObjectLib,
  "lib.es2019.string.d.ts": es2019StringLib,
  "lib.es2019.symbol.d.ts": es2019SymbolLib,

  "lib.es2020.d.ts": es2020Lib,
  "lib.es2020.bigint.d.ts": es2020BigintLib,
  "lib.es2020.date.d.ts": es2020DateLib,
  "lib.es2020.intl.d.ts": es2020IntlLib,
  "lib.es2020.number.d.ts": es2020NumberLib,
  "lib.es2020.promise.d.ts": es2020PromiseLib,
  "lib.es2020.sharedmemory.d.ts": es2020SharedmemoryLib,
  "lib.es2020.string.d.ts": es2020StringLib,
  "lib.es2020.symbol.wellknown.d.ts": es2020SymbolWellknownLib,

  "lib.es2021.d.ts": es2021Lib,
  "lib.es2021.intl.d.ts": es2021IntlLib,
  "lib.es2021.promise.d.ts": es2021PromiseLib,
  "lib.es2021.string.d.ts": es2021StringLib,
  "lib.es2021.weakref.d.ts": es2021WeakrefLib,

  "lib.es2022.d.ts": es2022Lib,
  "lib.es2022.array.d.ts": es2022ArrayLib,
  "lib.es2022.error.d.ts": es2022ErrorLib,
  "lib.es2022.intl.d.ts": es2022IntlLib,
  "lib.es2022.object.d.ts": es2022ObjectLib,
  "lib.es2022.regexp.d.ts": es2022RegexpLib,
  "lib.es2022.string.d.ts": es2022StringLib,

  "lib.es2023.d.ts": es2023Lib,
  "lib.es2023.array.d.ts": es2023ArrayLib,
  "lib.es2023.collection.d.ts": es2023CollectionLib,
  "lib.es2023.intl.d.ts": es2023IntlLib,

  "lib.es2024.d.ts": es2024Lib,
  "lib.es2024.arraybuffer.d.ts": es2024ArraybufferLib,
  "lib.es2024.collection.d.ts": es2024CollectionLib,
  "lib.es2024.object.d.ts": es2024ObjectLib,
  "lib.es2024.promise.d.ts": es2024PromiseLib,
  "lib.es2024.regexp.d.ts": es2024RegexpLib,
  "lib.es2024.sharedmemory.d.ts": es2024SharedmemoryLib,
  "lib.es2024.string.d.ts": es2024StringLib,
}

export default esLibDefs
